import { type ReactNode } from 'react'
import { ExternalLinkForm, ButtonAdd, Page, NotReady, PermissionCheckWrapper } from '@components'
import { useProfile, useAddExternalLink, getRandomColor, useExternalLinks, zodResolver } from '@front/model'
import { useToastError } from '@hooks'
import { Stack } from '@mantine/core'
import { useForm } from '@mantine/form'
import { notifications } from '@mantine/notifications'
import { useNavigate, useRouter, createFileRoute } from '@tanstack/react-router'
import { type ExternalLinkScopeId, type UnsavedExternalLink, UnsavedExternalLinkSchema } from '@terros/common'

type FormValues = UnsavedExternalLink

export const AddLink = (): ReactNode => {
  const { company, isLoading, error } = useProfile()

  if (isLoading || error || !company) {
    return <NotReady data={company} error='Link not found' id='Link' isLoading={isLoading} />
  }

  return (
    <PermissionCheckWrapper navigationTo='/' permissionType='externalLink:manage' targetId={company.companyId}>
      <AddLinkWithData scopeId={company.companyId} />
    </PermissionCheckWrapper>
  )
}

type AddLinkWithDataProps = {
  scopeId: ExternalLinkScopeId
}

const AddLinkWithData = ({ scopeId }: AddLinkWithDataProps): ReactNode => {
  const { add, isAdding, error } = useAddExternalLink()
  const { data } = useExternalLinks({ fetchAll: true })
  const router = useRouter()
  const navigate = useNavigate()

  useToastError(error)

  const form = useForm<FormValues>({
    initialValues: {
      url: '',
      label: '',
      description: '',
      icon: '',
      color: getRandomColor(),
      scopeId: scopeId,
      order: (data?.at(-1)?.order ?? 0) + 1,
    },
    validate: zodResolver(UnsavedExternalLinkSchema),
  })

  const handleSubmit = async (formValues: FormValues): Promise<void> => {
    if (!formValues.scopeId) return

    const { description, scopeId, ...rest } = formValues
    const linkData = {
      ...rest,
      scopeId,
      description: description,
    }

    await add(linkData)
    router.history.back()
    form.resetDirty(formValues)
    notifications.show({ message: 'Link added', color: 'green' })
  }

  return (
    <form className='w-full' onSubmit={form.onSubmit(handleSubmit)}>
      <Page>
        <Page.Heading back goBackBehavior='history' title='Add Link'>
          <ButtonAdd disabled={!form.isDirty()} loading={isAdding} type='submit' />
        </Page.Heading>
        <Page.Content scrollArea>
          <Stack gap='md'>
            <ExternalLinkForm form={form} />
          </Stack>
        </Page.Content>
      </Page>
    </form>
  )
}

export const Route = createFileRoute('/settings/links_/add/')({
  component: AddLink,
})
